import React, { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import type { EChartsOption } from 'echarts';
import { ChartComponentProps, getBaseOption, formatTooltipValue, ECHARTS_OPTS, FALLBACK_PALETTE } from './chartConfig';

/** Get numeric value from a row: prefer .value, else first numeric key (for multi-series / backend variance). */
function getNodeValue(d: Record<string, unknown>): number {
  if (d.value != null && typeof d.value === 'number' && !Number.isNaN(d.value)) return d.value;
  if (typeof d.value === 'string') {
    const n = parseFloat(d.value);
    if (!Number.isNaN(n)) return n;
  }
  const firstNum = Object.keys(d).find((k) => k !== 'name' && typeof d[k] === 'number');
  if (firstNum != null) return Number(d[firstNum]) || 0;
  return 0;
}

/** Recursively normalize a node to ECharts treemap shape: { name, value, children?, itemStyle? }. */
function normalizeNode(d: any): { name: string; value: number; children?: any[]; itemStyle?: { color: string } } {
  const value = getNodeValue(d);
  const name = String(d?.name ?? d?.label ?? d?.category ?? '');
  const children = Array.isArray(d?.children) ? d.children.map((c: any) => normalizeNode(c)) : undefined;
  const node: { name: string; value: number; children?: any[]; itemStyle?: { color: string } } = { name: name || 'Unnamed', value };
  if (children && children.length > 0) node.children = children;
  return node;
}

/** Assign a different color from the palette to each box (mutates nodes). */
function assignColorsToTreemapNodes(
  seriesData: { name: string; value: number; children?: any[]; itemStyle?: { color: string } }[],
  palette: string[],
): void {
  let index = 0;
  function visit(nodes: { itemStyle?: { color: string }; children?: any[] }[]): void {
    for (const node of nodes) {
      node.itemStyle = { ...node.itemStyle, color: palette[index % palette.length] };
      index += 1;
      if (node.children && node.children.length > 0) visit(node.children);
    }
  }
  // Single wrapper root (name: '') — color only its children so each visible box is distinct
  const singleRoot = seriesData.length === 1 && seriesData[0].name === '' && seriesData[0].children?.length;
  if (singleRoot && seriesData[0].children) {
    visit(seriesData[0].children);
  } else {
    visit(seriesData);
  }
}

export const TreemapChart: React.FC<ChartComponentProps> = ({ chart, height = 320, colors }) => {
  const option = useMemo((): EChartsOption => {
    const data = chart.data || [];
    if (data.length === 0) return {};

    const base = getBaseOption(colors);

    const nodes = data.map((d) => normalizeNode(d));

    // ECharts treemap expects a single root with children for flat lists; multiple roots also work but wrapping ensures consistent layout.
    const hasAnyChildren = nodes.some((n) => n.children && n.children.length > 0);
    const seriesData = hasAnyChildren
      ? nodes
      : [
          {
            name: '',
            value: nodes.reduce((sum, n) => sum + n.value, 0),
            children: nodes.filter((n) => n.value > 0 || n.name),
          },
        ];

    const palette = colors && colors.length > 0 ? colors : FALLBACK_PALETTE;
    assignColorsToTreemapNodes(seriesData, palette);

    return {
      ...base,
      tooltip: {
        ...(base.tooltip as object),
        trigger: 'item',
        formatter: (params: unknown) => {
          const p = params as { name?: string; value?: number; marker?: string };
          return `<div style="font-size:12px">${p.marker || ''}<b>${p.name ?? ''}</b><br/>${formatTooltipValue(p.value)}</div>`;
        },
      },
      series: [
        {
          type: 'treemap',
          data: seriesData,
          roam: false,
          breadcrumb: { show: true },
          label: { show: true, formatter: '{b}', fontSize: 12 },
          itemStyle: { borderColor: '#fff', borderWidth: 2, gapWidth: 2 },
          levels: [
            { itemStyle: { borderColor: '#fff', borderWidth: 3 } },
            { itemStyle: { borderColor: '#EDE6E1', borderWidth: 1 } },
          ],
        },
      ],
    };
  }, [chart, colors]);

  return <ReactECharts option={option} style={{ height }} notMerge lazyUpdate opts={ECHARTS_OPTS} />;
};
